import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Palette, Type, LayoutGrid, Save, RotateCcw, Plus, X, Lock,
  Component as ComponentIcon, Camera, Wand2, Sparkles, ShieldCheck,
  Smartphone, FileText, Loader2, Search, Upload, Trash2, Check,
  AlertTriangle, MousePointerClick,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiRequest, queryClient, parseJsonResponse, FRIENDLY_NETWORK_ERROR } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { emptyBrandDesignSystem, type Brand, type BrandDesignSystem, type BrandFont } from "@shared/schema";
import {
  BRAND_FONT_ROLE_OPTIONS,
  brandFontLabelForRole,
  brandFontRoleFromLabel,
  type BrandFontRole,
  BRAND_FONT_LICENSE_OPTIONS,
  brandFontLicenseWarning,
  isBrandFontLicenseFlagged,
  isUsableBrandFont,
  selectBrandButtonFont,
  type BrandFontLicenseStatus,
} from "@shared/brand-fonts";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  normalizeCtaHex,
  autoCtaTextColor,
  ctaShapeRadiusCss,
  type BrandCtaShape,
} from "@shared/brand-cta-style";
import { GOOGLE_FONT_CATALOG } from "@shared/google-fonts";
import {
  ensureGoogleFontsLoaded,
  ensureUploadedFontLoaded,
  previewFamilyFor,
} from "@/lib/brand-font-loader";
import {
  StyleRulesDialog,
  normalizeStyleRulesText,
  type StyleRulesValues,
} from "@/components/style-rules-dialog";

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function normalize(input: unknown): BrandDesignSystem {
  const base = emptyBrandDesignSystem();
  if (!input || typeof input !== "object") return base;
  const merged: any = { ...base, ...(input as object) };
  merged.typographyDirection = { ...base.typographyDirection, ...(merged.typographyDirection || {}) };
  merged.fonts = Array.isArray(merged.fonts) ? merged.fonts : [];
  merged.ctaStyle = { ...base.ctaStyle, ...(merged.ctaStyle || {}) };
  merged.layoutDirection = { ...base.layoutDirection, ...(merged.layoutDirection || {}) };
  merged.componentStyle = { ...base.componentStyle, ...(merged.componentStyle || {}) };
  merged.photographyDirection = { ...base.photographyDirection, ...(merged.photographyDirection || {}) };
  merged.imageGenerationRules = { ...base.imageGenerationRules, ...(merged.imageGenerationRules || {}) };
  merged.pageDesignRules = { ...base.pageDesignRules, ...(merged.pageDesignRules || {}) };
  // styleRulesFont is optional on older design-system blobs while the shared
  // contract rolls out. Keep the client draft shape stable in either case.
  merged.styleRules = typeof merged.styleRules === "string" ? merged.styleRules : "";
  merged.styleRulesFont = typeof merged.styleRulesFont === "string" ? merged.styleRulesFont : "";
  return merged as BrandDesignSystem;
}

type BrandDesignSystemWithFontRules = BrandDesignSystem & { styleRulesFont?: string };

function styleRulesValuesFor(value: BrandDesignSystem): StyleRulesValues {
  const designSystem = value as BrandDesignSystemWithFontRules;
  return {
    styleRules: typeof designSystem.styleRules === "string" ? designSystem.styleRules : "",
    styleRulesFont: typeof designSystem.styleRulesFont === "string" ? designSystem.styleRulesFont : "",
  };
}

// Brand-font preview loading (ensureGoogleFontsLoaded / ensureUploadedFontLoaded
// / previewFamilyFor) lives in @/lib/brand-font-loader, shared with the LP
// Express per-block font picker.

const FONT_SAMPLE_TEXT = "The quick brown fox jumps over the lazy dog";
const FONT_FILE_EXT = /\.(ttf|otf|woff2?)$/i;
const FONT_FILE_MAX = 5 * 1024 * 1024;

function newFontId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function BrandFontsEditor({
  fonts,
  onChange,
}: {
  fonts: BrandFont[];
  onChange: (next: BrandFont[]) => void;
}) {
  const { toast } = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Load all catalog families once the picker first opens so every option
  // renders in its own typeface.
  useEffect(() => {
    if (pickerOpen) ensureGoogleFontsLoaded(GOOGLE_FONT_CATALOG.map((f) => f.family));
  }, [pickerOpen]);

  const chosenGoogle = useMemo(
    () => new Set(fonts.filter((f) => f.source === "google").map((f) => f.family)),
    [fonts],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return GOOGLE_FONT_CATALOG;
    return GOOGLE_FONT_CATALOG.filter((f) => f.family.toLowerCase().includes(q));
  }, [search]);

  const addGoogleFont = (family: string) => {
    if (chosenGoogle.has(family)) return;
    onChange([
      ...fonts,
      {
        id: newFontId(),
        source: "google",
        family,
        label: "",
        url: null,
        fileName: null,
        format: null,
        licenseStatus: "open_source",
        rightsConfirmed: true,
      },
    ]);
    setPickerOpen(false);
    setSearch("");
  };

  const handleFontFile = async (file: File) => {
    if (!FONT_FILE_EXT.test(file.name)) {
      toast({
        title: "Not a font file",
        description: "Upload a TTF, OTF, WOFF, or WOFF2 font file.",
        variant: "destructive",
      });
      return;
    }
    if (file.size > FONT_FILE_MAX) {
      toast({ title: "File too large", description: "Maximum font file size is 5MB.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload/brand-font", { method: "POST", body: fd, credentials: "include" });
      const isJson = (res.headers.get("content-type") || "").includes("application/json");
      if (!res.ok) {
        // A non-JSON body means the request never reached the real endpoint
        // (e.g. it fell through to the SPA's index.html because the route wasn't
        // live yet) — show a friendly message instead of the raw HTML/parse error.
        let msg = FRIENDLY_NETWORK_ERROR;
        if (isJson) {
          try { msg = (await res.json()).message || "Upload failed"; } catch { msg = FRIENDLY_NETWORK_ERROR; }
        }
        throw new Error(msg);
      }
      const data = await parseJsonResponse<{ url: string; fileName?: string; format?: string | null }>(res);
      // Display name from the original file name: strip extension, prettify
      // separators (e.g. "Recoleta-SemiBold.otf" → "Recoleta SemiBold").
      const family = file.name.replace(FONT_FILE_EXT, "").replace(/[-_]+/g, " ").trim() || file.name;
      onChange([
        ...fonts,
        {
          id: newFontId(),
          source: "upload",
          family,
          label: "",
          url: data.url,
          fileName: data.fileName || file.name,
          format: data.format || null,
          licenseStatus: "unknown",
          rightsConfirmed: false,
        },
      ]);
    } catch (e: any) {
      toast({ title: "Font upload failed", description: e?.message || "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const updateFont = (id: string, patch: Partial<BrandFont>) => {
    onChange(fonts.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const removeFont = (id: string) => {
    onChange(fonts.filter((f) => f.id !== id));
  };

  return (
    <div className="space-y-3 pt-2 border-t border-border/40">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Brand fonts</Label>
          <p className="text-xs text-muted-foreground/80 mt-0.5">
            Pick a free font or upload your own (TTF, OTF, WOFF, WOFF2 — max 5MB).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="gap-1.5" data-testid="button-pick-free-font">
                <Plus className="h-3.5 w-3.5" /> Free font
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="p-2 border-b border-border/40">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search fonts…"
                    className="text-sm pl-8 h-8"
                    autoFocus
                    data-testid="input-font-search"
                  />
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto p-1">
                {filtered.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4" data-testid="text-no-fonts-found">
                    No fonts match "{search}"
                  </p>
                )}
                {filtered.map((f) => {
                  const chosen = chosenGoogle.has(f.family);
                  return (
                    <button
                      key={f.family}
                      type="button"
                      onClick={() => addGoogleFont(f.family)}
                      disabled={chosen}
                      className={`w-full flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left transition-colors ${
                        chosen ? "opacity-50 cursor-default" : "hover:bg-muted/60"
                      }`}
                      data-testid={`option-font-${f.family.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="min-w-0">
                        <span className="block text-base leading-tight truncate" style={{ fontFamily: `'${f.family}', sans-serif` }}>
                          {f.family}
                        </span>
                        <span className="text-[10px] text-muted-foreground capitalize">{f.category}</span>
                      </div>
                      {chosen && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid="button-upload-font"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? "Uploading…" : "Upload font"}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".ttf,.otf,.woff,.woff2"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFontFile(file);
              e.target.value = "";
            }}
            data-testid="input-font-file"
          />
        </div>
      </div>

      {fonts.length === 0 ? (
        <p className="text-xs text-muted-foreground/70 italic" data-testid="text-no-brand-fonts">
          No brand fonts yet.
        </p>
      ) : (
        <div className="space-y-2">
          {fonts.map((font) => (
            <div
              key={font.id}
              className="rounded-lg border border-border/40 bg-card px-3 py-2.5 space-y-1.5"
              data-testid={`card-brand-font-${font.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium truncate" data-testid={`text-font-name-${font.id}`}>
                  {font.family}
                </span>
                <Badge variant="secondary" className="text-[10px] rounded-full px-2 h-5 shrink-0">
                  {font.source === "google" ? "Free font" : `Uploaded${font.format ? ` · ${font.format.toUpperCase()}` : ""}`}
                </Badge>
                <div className="flex items-center gap-1.5 ml-auto">
                  <Select
                    value={brandFontRoleFromLabel(font.label)}
                    onValueChange={(role) =>
                      updateFont(font.id, {
                        label: brandFontLabelForRole(role as BrandFontRole),
                      })
                    }
                  >
                    <SelectTrigger
                      className="text-xs h-7 w-40"
                      data-testid={`select-font-label-${font.id}`}
                    >
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRAND_FONT_ROLE_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          data-testid={`option-font-role-${opt.value}-${font.id}`}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => removeFont(font.id)}
                    className="text-muted-foreground/50 hover:text-destructive transition-colors p-1"
                    aria-label={`Remove ${font.family}`}
                    data-testid={`button-remove-font-${font.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p
                className="text-lg leading-snug text-foreground/90 truncate"
                style={{ fontFamily: previewFamilyFor(font) }}
                data-testid={`text-font-preview-${font.id}`}
              >
                {FONT_SAMPLE_TEXT}
              </p>
              {/* Licensing / copyright — only for uploaded (custom/proprietary)
                  fonts. Google fonts are inherently open-licensed and never
                  need a status. */}
              {font.source === "upload" && (
                <div className="pt-1.5 mt-0.5 border-t border-border/30 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground shrink-0">
                      License
                    </Label>
                    <Select
                      value={font.licenseStatus ?? "unknown"}
                      onValueChange={(v) =>
                        updateFont(font.id, { licenseStatus: v as BrandFontLicenseStatus })
                      }
                    >
                      <SelectTrigger
                        className="text-xs h-7 w-48"
                        data-testid={`select-font-license-${font.id}`}
                      >
                        <SelectValue placeholder="License status" />
                      </SelectTrigger>
                      <SelectContent>
                        {BRAND_FONT_LICENSE_OPTIONS.map((opt) => (
                          <SelectItem
                            key={opt.value}
                            value={opt.value}
                            data-testid={`option-font-license-${opt.value}-${font.id}`}
                          >
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                      <Checkbox
                        checked={!!font.rightsConfirmed}
                        onCheckedChange={(c) =>
                          updateFont(font.id, { rightsConfirmed: c === true })
                        }
                        data-testid={`checkbox-font-rights-${font.id}`}
                      />
                      I have the rights to use this font
                    </label>
                  </div>
                  {brandFontLicenseWarning(font) && (
                    <p
                      className="flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-500"
                      data-testid={`text-font-license-warning-${font.id}`}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>{brandFontLicenseWarning(font)}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionCard({
  icon: Icon, title, description, children, testId,
}: { icon: any; title: string; description?: string; children: React.ReactNode; testId: string }) {
  return (
    <Card className="border-border/40" data-testid={testId}>
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
          </div>
        </div>
        <div className="space-y-4">{children}</div>
      </CardContent>
    </Card>
  );
}

function ComingSoonCard({
  icon: Icon, title, description, testId,
}: { icon: any; title: string; description: string; testId: string }) {
  return (
    <Card className="border-dashed border-border/50 bg-muted/10" data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-muted/40 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-muted-foreground/60" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground/70">{title}</h3>
              <Badge variant="secondary" className="text-[10px] rounded-full px-2 h-5 font-medium gap-1">
                <Lock className="h-2.5 w-2.5" /> Coming next
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TextField({
  label, value, onChange, placeholder, testId, multiline, maxLength,
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; testId: string; multiline?: boolean; maxLength?: number }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
      {multiline ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="text-sm resize-none"
          data-testid={testId}
          maxLength={maxLength}
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="text-sm"
          data-testid={testId}
          maxLength={maxLength}
        />
      )}
    </div>
  );
}

function ChipListEditor({
  label, values, onChange, placeholder, testId,
}: { label: string; values: string[]; onChange: (v: string[]) => void; placeholder?: string; testId: string }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(""); return; }
    onChange([...values, v]);
    setDraft("");
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={`${testId}-input`}
        />
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testId}-add`}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {values.map((v, i) => (
            <Badge
              key={`${v}-${i}`}
              variant="secondary"
              className="text-xs rounded-full px-2.5 py-1 gap-1.5 group"
              data-testid={`${testId}-chip-${i}`}
            >
              <span>{v}</span>
              <button
                type="button"
                onClick={() => onChange(values.filter((_, idx) => idx !== i))}
                className="text-muted-foreground/60 hover:text-destructive transition-colors"
                data-testid={`${testId}-remove-${i}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function ColorListEditor({
  label, values, onChange, testId,
}: { label: string; values: string[]; onChange: (v: string[]) => void; testId: string }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    let v = draft.trim();
    if (!v) return;
    if (!v.startsWith("#")) v = `#${v}`;
    if (!HEX_RE.test(v)) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) { setDraft(""); return; }
    onChange([...values, v.toUpperCase()]);
    setDraft("");
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="#F7EAD2"
          className="text-sm font-mono"
          data-testid={`${testId}-input`}
        />
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testId}-add`}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {values.map((hex, i) => (
            <div
              key={`${hex}-${i}`}
              className="flex items-center gap-2 rounded-lg border border-border/40 pl-1 pr-2 py-1 bg-card"
              data-testid={`${testId}-swatch-${i}`}
            >
              <div className="w-6 h-6 rounded-md border border-border/40 shrink-0" style={{ backgroundColor: hex }} />
              <span className="text-xs font-mono text-foreground/80">{hex}</span>
              <button
                type="button"
                onClick={() => onChange(values.filter((_, idx) => idx !== i))}
                className="text-muted-foreground/50 hover:text-destructive transition-colors"
                data-testid={`${testId}-remove-${i}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CTA / button style (Task #1233) ─────────────────────────────────────────
// Fill color (required to take effect), optional text color (auto-contrast
// when unset), and corner shape. Consumed by the Ads Express prompt directive
// via shared/brand-cta-style.ts. ADS-ONLY for now — LP/Email consumption is
// wired but disabled behind BRAND_CTA_SURFACES until a follow-up re-enables
// it, so the copy below must not promise landing pages or emails.
const CTA_SHAPE_OPTIONS: Array<{ value: BrandCtaShape; label: string }> = [
  { value: "square", label: "Square" },
  { value: "rounded", label: "Rounded" },
  { value: "pill", label: "Pill" },
];

function CtaStyleEditor({
  value,
  paletteColors,
  fonts,
  onChange,
}: {
  value: BrandDesignSystem["ctaStyle"];
  paletteColors: string[];
  fonts: BrandFont[];
  onChange: (next: BrandDesignSystem["ctaStyle"]) => void;
}) {
  const set = (patch: Partial<BrandDesignSystem["ctaStyle"]>) =>
    onChange({ ...value, ...patch });

  // Saved fonts the CTA label can use (uploads need a stored file to render).
  const usableFonts = fonts.filter(isUsableBrandFont);
  const selectedFont = value.fontId
    ? usableFonts.find((f) => f.id === value.fontId) ?? null
    : null;
  // With no explicit pick, the saved font assigned the "Buttons" role applies
  // (mirrors resolveBrandCtaStyle's fallback).
  const buttonRoleFont = selectBrandButtonFont(fonts);
  const effectiveFont = selectedFont ?? (value.fontId ? null : buttonRoleFont);
  // Load the effective font so the preview shows the real face.
  useEffect(() => {
    if (!effectiveFont) return;
    if (effectiveFont.source === "google") ensureGoogleFontsLoaded([effectiveFont.family]);
    else if (effectiveFont.url) ensureUploadedFontLoaded(effectiveFont.id, effectiveFont.url);
  }, [effectiveFont?.id]);

  const fill = normalizeCtaHex(value.fillColor);
  const isOutline = value.fill === "outline";
  // Preview is always visible while the section is on — before a fill color is
  // picked it uses the same default violet the color picker shows, so shape /
  // caps / font choices are never previewing into a blank space.
  const previewFill = fill ?? "#7c3aed";
  const preview = value.enabled
    ? {
        backgroundColor: isOutline ? "transparent" : previewFill,
        color:
          normalizeCtaHex(value.textColor) ??
          (isOutline ? previewFill : autoCtaTextColor(previewFill)),
        border: isOutline ? `2px solid ${previewFill}` : undefined,
        borderRadius: value.shape ? ctaShapeRadiusCss(value.shape) : "8px",
        textTransform: value.allCaps ? ("uppercase" as const) : undefined,
        fontFamily: effectiveFont ? previewFamilyFor(effectiveFont) : undefined,
      }
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="text-xs font-medium">Use a brand button style</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            When on, new ad runs style CTA buttons with these settings. (Landing pages and emails coming soon.)
          </p>
        </div>
        <Switch
          checked={value.enabled}
          onCheckedChange={(v) => set({ enabled: v })}
          data-testid="switch-cta-style-enabled"
        />
      </div>

      {value.enabled && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Button fill color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={fill ?? "#7c3aed"}
                  onChange={(e) => set({ fillColor: e.target.value })}
                  className="h-8 w-9 rounded-md border border-border/40 bg-transparent p-0.5 cursor-pointer"
                  data-testid="picker-cta-fill"
                />
                <Input
                  value={value.fillColor ?? ""}
                  onChange={(e) => set({ fillColor: e.target.value.trim() || null })}
                  placeholder="#7C3AED"
                  className="text-sm font-mono"
                  data-testid="input-cta-fill"
                />
              </div>
              {paletteColors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {paletteColors.map((hex, i) => (
                    <button
                      key={`${hex}-${i}`}
                      type="button"
                      title={hex}
                      onClick={() => set({ fillColor: hex })}
                      className={`w-6 h-6 rounded-md border transition-shadow ${fill === normalizeCtaHex(hex) ? "ring-2 ring-primary border-transparent" : "border-border/40"}`}
                      style={{ backgroundColor: hex }}
                      data-testid={`swatch-cta-fill-${i}`}
                    />
                  ))}
                </div>
              )}
              {value.fillColor && !fill && (
                <p className="text-[11px] text-destructive">Enter a valid hex color (e.g. #7C3AED). Until then the brand style is ignored.</p>
              )}
              {!value.fillColor && (
                <p className="text-[11px] text-muted-foreground">Required — without a fill color the brand style is ignored.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Button text color</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={value.textColor ? "custom" : "auto"}
                  onValueChange={(v) => {
                    if (v === "auto") set({ textColor: null });
                    else set({ textColor: normalizeCtaHex(value.textColor) ?? (fill ? autoCtaTextColor(fill) : "#ffffff") });
                  }}
                >
                  <SelectTrigger className="w-[130px] text-xs" data-testid="select-cta-text-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatic</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {value.textColor !== null && (
                  <>
                    <input
                      type="color"
                      value={normalizeCtaHex(value.textColor) ?? "#ffffff"}
                      onChange={(e) => set({ textColor: e.target.value })}
                      className="h-8 w-9 rounded-md border border-border/40 bg-transparent p-0.5 cursor-pointer"
                      data-testid="picker-cta-text"
                    />
                    <Input
                      value={value.textColor ?? ""}
                      onChange={(e) => set({ textColor: e.target.value.trim() || null })}
                      placeholder="#FFFFFF"
                      className="text-sm font-mono"
                      data-testid="input-cta-text"
                    />
                  </>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Automatic picks white or near-black based on the fill color.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Fill style</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={!isOutline ? "default" : "outline"}
                  onClick={() => set({ fill: "solid" })}
                  data-testid="button-cta-fill-solid"
                >
                  Solid
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={isOutline ? "default" : "outline"}
                  onClick={() => set({ fill: "outline" })}
                  data-testid="button-cta-fill-outline"
                >
                  Outline
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Outline draws a transparent button with a border in the fill color.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Corner shape</Label>
              <div className="flex items-center gap-2">
                {CTA_SHAPE_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={value.shape === opt.value ? "default" : "outline"}
                    onClick={() => set({ shape: value.shape === opt.value ? null : opt.value })}
                    data-testid={`button-cta-shape-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Optional — leave unselected to keep each tool's default corners.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Button font</Label>
              <Select
                value={value.fontId ?? "inherit"}
                onValueChange={(v) => set({ fontId: v === "inherit" ? null : v })}
              >
                <SelectTrigger className="text-xs" data-testid="select-cta-font">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">
                    {buttonRoleFont
                      ? `${buttonRoleFont.family} (Buttons font)`
                      : "Match each design (default)"}
                  </SelectItem>
                  {usableFonts.map((f) => (
                    <SelectItem key={f.id} value={f.id} data-testid={`option-cta-font-${f.id}`}>
                      {f.family}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {usableFonts.length === 0
                  ? "Save fonts in the Fonts section above to pick one for button labels."
                  : buttonRoleFont
                    ? `Defaults to ${buttonRoleFont.family} — the font assigned the Buttons role above.`
                    : "Uses one of this brand's saved fonts for button labels. Tip: assign a font the Buttons role above to make it the default."}
              </p>
              {value.fontId && !selectedFont && (
                <p className="text-[11px] text-destructive">
                  {buttonRoleFont
                    ? `The selected font is no longer saved — buttons use ${buttonRoleFont.family} (the Buttons font) instead.`
                    : "The selected font is no longer saved — buttons fall back to each design's font."}
                </p>
              )}
            </div>

            <div className="flex items-start justify-between gap-3 pt-1">
              <div>
                <Label className="text-xs font-medium">All caps labels</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Write button labels in ALL CAPITAL LETTERS.
                </p>
              </div>
              <Switch
                checked={value.allCaps === true}
                onCheckedChange={(v) => set({ allCaps: v })}
                data-testid="switch-cta-all-caps"
              />
            </div>
          </div>

          {preview && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Preview</Label>
              <div className="rounded-lg border border-border/40 bg-muted/30 p-5 flex items-center justify-center">
                <span
                  className="inline-flex items-center px-6 py-2.5 text-sm font-semibold select-none"
                  style={preview}
                  data-testid="preview-cta-button"
                >
                  Shop Now
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function BrandDesignSystemEditor({
  value,
  onChange,
  showHeader = false,
  showCtaStyle = false,
  styleRulesDialog,
}: {
  value: unknown;
  onChange: (next: BrandDesignSystem) => void;
  showHeader?: boolean;
  // Task #1529 — the CTA/button style editor is gated behind the
  // `brand_cta_style` beta flag, which is BRAND-LEVEL: enabling the feature
  // for a brand (Ads Express admin → Features, or Admin → Beta Features)
  // shows the editor to every user of that brand. FAIL-CLOSED default:
  // callers must explicitly pass true after checking the brand's flags
  // (BrandDesignSystemTab and the brand wizard's Design System step both
  // do — the wizard only once the brand exists, so during initial
  // creation, before any flags can exist, the section stays hidden).
  showCtaStyle?: boolean;
  // The standard rules editor is a modal owned by the page that persists the
  // complete brand draft. Rendering it here keeps its trigger beside the
  // Buttons & CTAs section without bringing back an inline beta section.
  styleRulesDialog?: React.ReactNode;
}) {
  const draft = useMemo(() => normalize(value), [value]);

  const update = <K extends keyof BrandDesignSystem>(key: K, v: BrandDesignSystem[K]) => {
    onChange({ ...draft, [key]: v });
  };
  const updateTypography = (k: keyof BrandDesignSystem["typographyDirection"], v: string) => {
    onChange({ ...draft, typographyDirection: { ...draft.typographyDirection, [k]: v } });
  };
  const updateLayout = <K extends keyof BrandDesignSystem["layoutDirection"]>(k: K, v: BrandDesignSystem["layoutDirection"][K]) => {
    onChange({ ...draft, layoutDirection: { ...draft.layoutDirection, [k]: v } });
  };

  return (
    <div className="space-y-5">
      {showHeader && (
        <div>
          <h2 className="text-sm font-semibold text-foreground">Brand Design System</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Visual source of truth for landing pages, ads, image prompts, and creative.
          </p>
        </div>
      )}

      <SectionCard
        icon={Palette}
        title="Brand Look"
        description="Positioning, mood, and the colors that define how this brand should feel."
        testId="design-section-brand-look"
      >
        <TextField
          label="Design positioning"
          value={draft.designPositioning}
          onChange={(v) => update("designPositioning", v)}
          placeholder="e.g. Ranch-crafted natural skincare with a warm farmstand feel."
          testId="input-design-positioning"
          multiline
        />
        <ChipListEditor
          label="Visual mood"
          values={draft.visualMood}
          onChange={(v) => update("visualMood", v)}
          placeholder="warm, rustic, honest…"
          testId="chips-visual-mood"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ColorListEditor
            label="Primary colors"
            values={draft.primaryColors}
            onChange={(v) => update("primaryColors", v)}
            testId="colors-primary"
          />
          <ColorListEditor
            label="Secondary colors"
            values={draft.secondaryColors}
            onChange={(v) => update("secondaryColors", v)}
            testId="colors-secondary"
          />
          <ColorListEditor
            label="Accent colors"
            values={draft.accentColors}
            onChange={(v) => update("accentColors", v)}
            testId="colors-accent"
          />
        </div>
      </SectionCard>

      <SectionCard
        icon={Type}
        title="Typography"
        description="How the brand sounds visually — headlines, body, and accent treatments."
        testId="design-section-typography"
      >
        <TextField
          label="Headline style"
          value={draft.typographyDirection.headlineStyle}
          onChange={(v) => updateTypography("headlineStyle", v)}
          placeholder="e.g. Bold sturdy serif or western-inspired display font."
          testId="input-headline-style"
          multiline
        />
        <TextField
          label="Body style"
          value={draft.typographyDirection.bodyStyle}
          onChange={(v) => updateTypography("bodyStyle", v)}
          placeholder="e.g. Clean readable sans-serif with strong mobile readability."
          testId="input-body-style"
          multiline
        />
        <TextField
          label="Accent style"
          value={draft.typographyDirection.accentStyle}
          onChange={(v) => updateTypography("accentStyle", v)}
          placeholder="e.g. Small uppercase farmstand label style."
          testId="input-accent-style"
          multiline
        />
        <BrandFontsEditor
          fonts={draft.fonts}
          onChange={(v) => update("fonts", v)}
        />
      </SectionCard>

      {showCtaStyle && (
      <SectionCard
        icon={MousePointerClick}
        title="Buttons & CTAs"
        description="Fill, text color, shape, font, and casing for CTA buttons in new ad runs. (Landing pages and emails coming soon.)"
        testId="design-section-cta-style"
      >
        <CtaStyleEditor
          value={draft.ctaStyle}
          paletteColors={Array.from(
            new Set(
              [...draft.primaryColors, ...draft.secondaryColors, ...draft.accentColors].filter(
                (h) => normalizeCtaHex(h),
              ),
            ),
          )}
          fonts={draft.fonts}
          onChange={(v) => update("ctaStyle", v)}
        />
      </SectionCard>
      )}

      {styleRulesDialog}

      <SectionCard
        icon={LayoutGrid}
        title="Layout"
        description="Page structure, density, and the layouts to embrace or avoid."
        testId="design-section-layout"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="Overall layout style"
            value={draft.layoutDirection.overallStyle}
            onChange={(v) => updateLayout("overallStyle", v)}
            placeholder="e.g. Mobile-first direct-response page."
            testId="input-overall-style"
            multiline
          />
          <TextField
            label="Section density"
            value={draft.layoutDirection.sectionDensity}
            onChange={(v) => updateLayout("sectionDensity", v)}
            placeholder="e.g. Medium density. Scannable, visual."
            testId="input-section-density"
            multiline
          />
        </div>
        <ChipListEditor
          label="Preferred layouts"
          values={draft.layoutDirection.preferredLayouts}
          onChange={(v) => updateLayout("preferredLayouts", v)}
          placeholder="two-column hero, receipt-style offer box…"
          testId="chips-preferred-layouts"
        />
        <ChipListEditor
          label="Layouts to avoid"
          values={draft.layoutDirection.avoidLayouts}
          onChange={(v) => updateLayout("avoidLayouts", v)}
          placeholder="sterile medical website, luxury spa layout…"
          testId="chips-avoid-layouts"
        />
      </SectionCard>

      <div className="pt-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Coming next</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ComingSoonCard
            icon={ComponentIcon}
            title="Components"
            description="Buttons, cards, badges, forms, offer boxes, comparison tables, testimonials, FAQ accordions."
            testId="coming-soon-components"
          />
          <ComingSoonCard
            icon={Camera}
            title="Photography"
            description="Photo style, lighting, settings, model direction, product and lifestyle shots, what to avoid."
            testId="coming-soon-photography"
          />
          <ComingSoonCard
            icon={Wand2}
            title="Image Generation Rules"
            description="Must-include cues, things to avoid, style keywords, and negative prompt rules."
            testId="coming-soon-image-rules"
          />
          <ComingSoonCard
            icon={Sparkles}
            title="Brand Motifs"
            description="Repeated visual elements, textures, symbols, badges, patterns, or design cues."
            testId="coming-soon-motifs"
          />
          <ComingSoonCard
            icon={ShieldCheck}
            title="Trust Visuals"
            description="Guarantee, shipping, review, certification, and small-batch badges."
            testId="coming-soon-trust"
          />
          <ComingSoonCard
            icon={Smartphone}
            title="Mobile Design Rules"
            description="Sticky CTA, hero image, offer placement, mobile section density, mobile CTA behavior."
            testId="coming-soon-mobile"
          />
          <ComingSoonCard
            icon={FileText}
            title="Page Design Rules"
            description="Hero, offer, proof, comparison, and CTA section rules."
            testId="coming-soon-page"
          />
        </div>
      </div>
    </div>
  );
}

export function BrandDesignSystemTab({ brand }: { brand: Brand }) {
  const { toast } = useToast();
  const initial = useMemo(
    () => normalize(brand.brandDesignSystem),
    [brand.id, brand.brandDesignSystem],
  );
  const [draft, setDraft] = useState<BrandDesignSystem>(() => deepClone(initial));
  const [styleRulesOpen, setStyleRulesOpen] = useState(false);
  const targetRef = useRef<{ brandId: string; epoch: number }>({
    brandId: brand.id,
    epoch: 0,
  });
  // A brand can change and then change back while a PATCH is in flight. The
  // epoch makes an old completion unable to hydrate the newly active target.
  if (targetRef.current.brandId !== brand.id) {
    targetRef.current = {
      brandId: brand.id,
      epoch: targetRef.current.epoch + 1,
    };
  }

  // Task #1529 — brand beta flags (managed on Admin → Beta Features / the
  // Ads Express admin Features tab). `brand_cta_style` is reported
  // BRAND-LEVEL by the endpoint (on for everyone once the brand has the
  // feature enabled). Gates the CTA/button style editor section;
  // fail-closed while loading or when the feature is off.
  const { data: betaFlags } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brand.id}/beta-features`],
  });
  const showCtaStyle = betaFlags?.brand_cta_style === true;

  useEffect(() => { setDraft(deepClone(initial)); }, [brand.id, initial]);
  useEffect(() => { setStyleRulesOpen(false); }, [brand.id]);

  const dirty = !deepEqual(draft, initial);

  const saveMutation = useMutation({
    mutationFn: async ({
      brandId,
      designSystem,
      expectedBrandDesignSystem,
    }: {
      brandId: string;
      epoch: number;
      designSystem: BrandDesignSystem;
      expectedBrandDesignSystem?: unknown;
    }) => {
      return apiRequest("PATCH", `/api/brands/${brandId}`, {
        brandDesignSystem: designSystem,
        ...(expectedBrandDesignSystem !== undefined ? { expectedBrandDesignSystem } : {}),
      });
    },
    onSuccess: (_response, variables) => {
      if (
        targetRef.current.brandId !== variables.brandId
        || targetRef.current.epoch !== variables.epoch
      ) {
        return;
      }
      toast({ title: "Brand Design System saved", description: "All future generators can use these rules." });
      queryClient.invalidateQueries({ queryKey: ["/api/brands", variables.brandId] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands"] });
    },
    onError: (e: any, variables) => {
      if (
        targetRef.current.brandId !== variables.brandId
        || targetRef.current.epoch !== variables.epoch
      ) {
        return;
      }
      toast({ title: "Save failed", description: e?.message ?? "Could not save design system.", variant: "destructive" });
    },
  });

  const persistableDraft = (value: BrandDesignSystem): BrandDesignSystem => {
    const next = {
      ...value,
      styleRules: normalizeStyleRulesText(value.styleRules),
      styleRulesFont: normalizeStyleRulesText(
        (value as BrandDesignSystemWithFontRules).styleRulesFont,
      ),
    };
    return next as BrandDesignSystem;
  };

  const saveDraft = (value: BrandDesignSystem) => {
    const designSystem = persistableDraft(value);
    saveMutation.mutate({
      brandId: brand.id,
      epoch: targetRef.current.epoch,
      designSystem,
    });
  };

  const saveStyleRules = async (values: StyleRulesValues) => {
    const target = {
      brandId: brand.id,
      epoch: targetRef.current.epoch,
    };
    const next = {
      ...draft,
      styleRules: values.styleRules,
      styleRulesFont: values.styleRulesFont,
    } as BrandDesignSystem;
    const designSystem = persistableDraft(next);
    try {
      await saveMutation.mutateAsync({
        ...target,
        designSystem,
        expectedBrandDesignSystem: brand.brandDesignSystem ?? null,
      });
    } catch (error) {
      if (
        targetRef.current.brandId !== target.brandId
        || targetRef.current.epoch !== target.epoch
      ) {
        return false;
      }
      throw error;
    }
    const currentTarget = (
      targetRef.current.brandId === target.brandId
      && targetRef.current.epoch === target.epoch
    );
    if (currentTarget) setDraft(designSystem);
    return currentTarget;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Brand Design System</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Visual source of truth for landing pages, ads, image prompts, and creative.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDraft(deepClone(initial))}
              data-testid="button-design-system-reset"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Discard
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => saveDraft(draft)}
            disabled={!dirty || saveMutation.isPending}
            data-testid="button-design-system-save"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            {dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      </div>

      <BrandDesignSystemEditor
        value={draft}
        onChange={setDraft}
        showCtaStyle={showCtaStyle}
        styleRulesDialog={
          <StyleRulesDialog
            values={styleRulesValuesFor(draft)}
            open={styleRulesOpen}
            onOpenChange={setStyleRulesOpen}
            onSave={saveStyleRules}
            isSaving={saveMutation.isPending}
            saveTestId="button-save-style-rules"
            discardTestId="button-discard-style-rules"
            trigger={
              <Button
                type="button"
                variant="outline"
                className="gap-1.5"
                data-testid="button-open-style-rules"
              >
                <FileText className="h-4 w-4" />
                Style Rules
              </Button>
            }
          />
        }
      />
    </div>
  );
}
